import { Box, Typography } from '@mui/material';
import ActionButtons from 'components/action-buttons';
import Drawer from 'components/drawer';
import Paper from 'components/paper';

import { ChevronRight } from 'lucide-react';
import { PropsWithChildren, useState } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import DailyConsumptionRate from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/input-settings/daily-consumption/daily-consumption-rate';
import EditDailyConsumption from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/input-settings/daily-consumption/edit-daily-consumption';
import FeedStrategyDialog from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/input-settings/daily-consumption/feed-strategy-dialog';
import { useInputSettingsState } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/context/input-settings-context';
import { useUpdateOFDSettings } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/hooks/use-update-ofd-settings';
import { ConsumptionAdjustmentMethod } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/consumption-adjustment-method.enum';
import { UpdateConsumptionRateInput } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/ofd-settings-input.type';
import { useGoogleTagManager } from 'views/order-feed/features/my-supply-plan/hooks/use-google-tag-manager';

type Props = PropsWithChildren & {};

const PAGE_NAME_ID = 'orderFeed.ofdMySupplyPlan';

const ConsumptionRateDialog: React.FC<Props> = ({ }) => {
  const intl = useIntl();

  const {
    getOfdSettingsQuery: { data: ofdSettings, refetch: refetchOfdSettings },
    dailyConsumptionInput,
  } = useInputSettingsState();

  const { createInputSettingsEvent } = useGoogleTagManager();
  const { updateDailyConsumptionRate } = useUpdateOFDSettings();

  // #region STATES
  const [edit, setEdit] = useState(false);

  // #endregion STATES

  if (!ofdSettings) return null;

  const { currentConsumptionRate } = ofdSettings;

  // #region HANDLERS
  const handleEdit = (edited: boolean) => setEdit(edited);

  const handleCancel = () => setEdit(false);

  const handleSave = () => {
    if (!dailyConsumptionInput) return;

    updateDailyConsumptionRate.mutate(dailyConsumptionInput, {
      onSuccess: () => {
        if (ofdSettings) {
          let oldValue2 = '';
          let newValue2 = '';

          switch (dailyConsumptionInput?.consumptionRateAdjustmentMethod) {
            case ConsumptionAdjustmentMethod.historicalAverage:
              oldValue2 = String(ofdSettings.consumptionRateAdjustmentDays);
              newValue2 = String(dailyConsumptionInput.consumptionRateAdjustmentDays);
              break;
            case ConsumptionAdjustmentMethod.manualCoefficient:
              oldValue2 = String(ofdSettings.manualConsumptionRateCoefficient ?? '1');
              newValue2 = String(dailyConsumptionInput.manualConsumptionRateCoefficient);
              break;
            case ConsumptionAdjustmentMethod.historicalAverageIgnoreStarvation:
              oldValue2 = String(ofdSettings.consumptionRateAdjustmentDays);
              newValue2 = String(dailyConsumptionInput.consumptionRateAdjustmentDays);
              break;
          }

          createInputSettingsEvent('save_daily_consumption_rate', {
            oldValue1: ofdSettings.consumptionRateAdjustmentMethod as ConsumptionAdjustmentMethod,
            newValue1: dailyConsumptionInput?.consumptionRateAdjustmentMethod as ConsumptionAdjustmentMethod,
            oldValue2: oldValue2,
            newValue2: newValue2,
            section: 'modal',
          });
          refetchOfdSettings();
          setEdit(false);
        }
      },
    });
  };
  // #endregion HANDLERS

  return (
    <Paper transparent sx={{ borderRadius: 2, mb: 6 }}>
      <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
        <Box display={'flex'} alignItems={'center'} gap={2}>
          <Typography display={'flex'}>
            <FormattedMessage id={`${PAGE_NAME_ID}.cards.consumptionRate`} />
          </Typography>
        </Box>
      </Box>
      <Box mt={2} display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
        <Box display={'flex'} alignItems={'center'}>
          <Typography fontSize={23} fontWeight={'bold'}>
            <FormattedNumber
              value={currentConsumptionRate}
              unit={'kilogram'}
              style="unit"
              minimumFractionDigits={0}
              maximumFractionDigits={0}
            />
          </Typography>
          <Typography>
            <FormattedMessage id={`${PAGE_NAME_ID}.cards.day`} />
          </Typography>
        </Box>
        <Drawer
          title={intl.formatMessage({ id: `${PAGE_NAME_ID}.cards.drawer.dailyConsumptionRate` })}
          actionButtons={<ActionButtons isEditing={edit} onEdit={handleEdit} onCancel={handleCancel} onSave={handleSave} />}
          button={
            <Typography sx={{ cursor: 'pointer' }} display={'flex'} gap={1} alignItems={'center'}>
              <FormattedMessage id={`${PAGE_NAME_ID}.cards.buttons.openConsumption`} />
              <ChevronRight size={18} />
            </Typography>
          }>
          <Box width={750} display={'flex'} flexDirection={'column'} gap={4}>
            <Typography>{intl.formatMessage({ id: `${PAGE_NAME_ID}.cards.drawer.calculateConsumption` })}</Typography>
            {edit ? <EditDailyConsumption /> : <DailyConsumptionRate />}
            <FeedStrategyDialog />
          </Box>
        </Drawer>
      </Box>
    </Paper>
  );
};

export default ConsumptionRateDialog;
